import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "../components/common/DataTable";
import StatusBadge from "../components/common/StatusBadge";
import { useAdminApi } from "../hooks/useAdminApi";
import { OrderSummary } from "../types/admin";

interface OrderRow {
	id: number;
	external_id?: string;
	buyer?: string;
	event?: string;
	total: number;
	status: string;
	created_at?: string | null;
}

const OrdersPage: React.FC = () => {
	const api = useAdminApi();
	const [filters, setFilters] = useState({ id: "", buyer: "", status: "" });
	const [rows, setRows] = useState<OrderRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const mapOrder = useCallback(
		(o: OrderSummary): OrderRow => ({
			id: o.id,
			external_id: o.external_id,
			buyer: o.customer_email ?? o.user?.email,
			event: o.event?.title,
			total: Number(o.total_amount ?? 0),
			status: o.status,
			created_at: o.created_at,
		}),
		[]
	);

	const fetchOrders = useCallback(
		async (pageOverride?: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await api.listOrders({
					external_id: filters.id || undefined,
					email: filters.buyer || undefined,
					status: filters.status || undefined,
					page: pageOverride ?? page,
					per_page: pageSize,
				});
				const mapped = res.data.map(mapOrder);
				setRows(mapped);
				setTotal(res.total);
				setPageSize(res.per_page);
			} catch (err) {
				console.warn("Failed to load orders", err);
				setError("注文一覧の取得に失敗しました");
			} finally {
				setLoading(false);
			}
		},
		[api, filters, page, pageSize, mapOrder]
	);

	useEffect(() => {
		fetchOrders();
	}, [fetchOrders]);

	const columns = useMemo(
		() => [
			{ key: "external_id", header: "注文ID" },
			{ key: "buyer", header: "購入者" },
			{ key: "event", header: "イベント" },
			{
				key: "total",
				header: "合計",
				align: "right" as const,
				render: (row: OrderRow): React.ReactNode =>
					row.total ? `¥${row.total.toLocaleString()}` : "-",
			},
			{
				key: "status",
				header: "決済",
				render: (row: OrderRow) => (
					<StatusBadge
						label={row.status}
						tone={
							row.status === "paid"
								? "green"
								: row.status === "pending"
								? "yellow"
								: "red"
						}
					/>
				),
			},
			{ key: "created_at", header: "購入日" },
		],
		[]
	);

	const handleFilter = () => {
		setPage(1);
		fetchOrders(1);
	};

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-xl font-semibold text-ink">Orders</h1>
				<p className="text-sm text-muted">
					期間/イベント/購入者で検索、QR 確認、再送メール
				</p>
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="grid gap-3 md:grid-cols-5">
					<input
						className="input"
						placeholder="注文ID"
						value={filters.id}
						onChange={(e) => setFilters({ ...filters, id: e.target.value })}
					/>
					<input
						className="input"
						placeholder="購入者メール"
						value={filters.buyer}
						onChange={(e) => setFilters({ ...filters, buyer: e.target.value })}
					/>
					<select
						className="input"
						value={filters.status}
						onChange={(e) => setFilters({ ...filters, status: e.target.value })}
					>
						<option value="">決済状態</option>
						<option value="paid">paid</option>
						<option value="pending">pending</option>
						<option value="failed">failed</option>
					</select>
					<div className="md:col-span-2 flex items-center gap-2">
						<button
							className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
							onClick={handleFilter}
						>
							検索
						</button>
						<span className="text-xs text-muted">
							external_id / email / status
						</span>
					</div>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						total={total}
						page={page}
						pageSize={pageSize}
						onPageChange={setPage}
						loading={loading}
						error={error}
					/>
				</div>
			</div>
		</div>
	);
};

export default OrdersPage;
